/**
 * Person-first accounts on the admin (contract
 * docs/person-first-accounts-contract-2026-10-29.md in the portal repo: P2,
 * P3, P6, P7, "Company status").
 *
 * THE STATUSES ARE THE SERVER'S. POST get-user-details-byid answers with the
 * profile's `accounts` block as a TOP-LEVEL key next to `data` (api WP4b:
 * superAdmin.getUserDetailsById adds companyStatus.loadAccountsBlock, the same
 * block as the portal profile read; a failed read is null). The admin reads
 * `accounts.person.status` and `accounts.company.status` from it and never
 * recomputes either predicate. No block (an API before WP4b, or its degraded
 * read) means "status unavailable", never a status computed from the flags.
 *
 * D-5 (the order screen). The admin order detail (POST
 * admin/get-myinvestment-details-by-id) carries no accounts block, so two reads
 * stay local there, each mirroring the API:
 *   - orderConfirmed: the order is confirmed when Number(status) === 1, the
 *     API's orderTransition.isCounted (what confirm's D-5 and recordSettlement
 *     read). Never inferred from paymentStatus.
 *   - companyCheckOf: the strict company predicate (api
 *     common/companyVerified.companyVerifiedStrict: the flag, no expiry, no
 *     reviewer message) with the placeholder rule, FAIL CLOSED: when the
 *     detail does not send the expiry or the message, the answer is
 *     NOT_STATED, never VERIFIED on comp_isVerified alone.
 */

/** accounts.person.status values (api companyStatus.PERSON_STATUS). */
export const PERSON_STATUS_LABELS = Object.freeze({
  VERIFIED: "Verified",
  NOT_VERIFIED: "Not verified",
  REJECTED: "Rejected",
  EXPIRED: "Expired",
});
/** accounts.company.status values other than NONE (api companyStatus.STATUS). */
export const COMPANY_STATUS_LABELS = Object.freeze({
  DRAFT: "Draft, not submitted for review",
  SUBMITTED: "Submitted for review",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
  NEEDS_INFO: "More information requested",
  EXPIRED: "Expired",
});
export const PERSON_STATUS_UNAVAILABLE = "Person status unavailable";
export const COMPANY_STATUS_UNAVAILABLE = "Company status unavailable";

const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * The placeholder rule: a message on file is a non-blank string that is not a
 * FormData placeholder. Blank, 'null', 'undefined' and 'NaN' (trimmed, any
 * case) count as no message.
 */
export function messageOnFile(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim().toLowerCase();
  return s !== "" && s !== "null" && s !== "undefined" && s !== "nan";
}

/** The message as it is when it is on file, else "" (a placeholder is never prefilled or shown). */
export function noteOnFile(value) {
  return messageOnFile(value) ? String(value) : "";
}

/** The `accounts` block of a get-user-details-byid answer (the body, as the reducer stores it), or null. */
export function accountsOf(answer) {
  const block = isPlainObject(answer) ? answer.accounts : null;
  return isPlainObject(block) ? block : null;
}

const statusOf = (part) => {
  const status = isPlainObject(part) ? part.status : null;
  return typeof status === "string" && status.trim() !== "" ? status : null;
};

/** The server's person status, or null when the answer carries none. */
export function personStatusOf(answer) {
  const block = accountsOf(answer);
  return block ? statusOf(block.person) : null;
}

/** The server's company status, or null when the answer carries none. */
export function companyStatusOf(answer) {
  const block = accountsOf(answer);
  return block ? statusOf(block.company) : null;
}

/** The decision a KYC form preselects for a server status: only one the form can record, else none. */
const PRESELECTABLE = Object.freeze(["VERIFIED", "REJECTED", "EXPIRED"]);
export function decisionPreselect(status) {
  return PRESELECTABLE.includes(status) ? status : "";
}

/** D-5: the order is confirmed (counted). The API's orderTransition.isCounted: Number(status) === 1. */
export function orderConfirmed(order) {
  return !!order && Number(order.status) === 1;
}

export const COMPANY_CHECK = Object.freeze({
  VERIFIED: "VERIFIED",
  NOT_VERIFIED: "NOT_VERIFIED",
  NOT_STATED: "NOT_STATED",
});
// users.comp_isVerified is a nullable BOOLEAN; a raw TINYINT reads 1 or "1".
const FLAG_SET = Object.freeze([true, 1, "1"]);
// users.comp_isIdExpired: only these read as no expiry; anything else is one (fail closed).
const NO_EXPIRY = Object.freeze([false, 0, null]);
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * D-5: the strict company predicate on the order's user, fail closed.
 *   -> VERIFIED      the flag set, no expiry, no reviewer message on file;
 *      NOT_VERIFIED  the flag not set, or an expiry, or a reviewer message;
 *      NOT_STATED    no user, or the flag set but the expiry or the message
 *                    not sent with the order (the API gap of the admin order
 *                    detail), so the predicate cannot be read.
 */
export function companyCheckOf(order) {
  const user = order && isPlainObject(order.user) ? order.user : null;
  if (!user) return COMPANY_CHECK.NOT_STATED;
  if (!FLAG_SET.includes(user.comp_isVerified)) return COMPANY_CHECK.NOT_VERIFIED;
  if (!has(user, "comp_isIdExpired") || !has(user, "comp_verification_error_message")) return COMPANY_CHECK.NOT_STATED;
  if (!NO_EXPIRY.includes(user.comp_isIdExpired)) return COMPANY_CHECK.NOT_VERIFIED;
  if (messageOnFile(user.comp_verification_error_message)) return COMPANY_CHECK.NOT_VERIFIED;
  return COMPANY_CHECK.VERIFIED;
}
